import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode, type TouchEvent } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { getProjectMembers } from '../transport/api'
import { setBoardLock, type EditMode } from '../chatv1/transport'
import { EditLock, mayChange } from './EditLock'
import { Icon } from '../ui/Icon'
import { BoardCanvas } from './BoardCanvas'
import { BoardMenu, MAIN_NAME, useBoards } from './BoardMenu'
import { BoardChat, useRoomChat } from './BoardChat'
import { mentionRowsOfMembers } from '../agents/AgentMention'
import { MAIN_BOARD, onBoardNav, openBoardSel, selectBoard } from './boardNav'
import { useBoard, type Activity } from './useBoard'
import { isNews, useMarkSeen } from './boardSeen'
import { chatv1SelfUser } from '../chatv1/auth'
import './board.css'

/** "claude-agent added 6 items" — who, in the room's own names, and what. */
export function activityText(a: Activity, nameOf: (user: string, agent?: string) => string): string {
  const who = nameOf(a.by, a.via)
  const parts: string[] = []
  const n = (k: number, what: string) => `${what} ${k} item${k === 1 ? '' : 's'}`
  if (a.added) parts.push(n(a.added, 'added'))
  if (a.removed) parts.push(n(a.removed, 'removed'))
  if (a.changed && !a.added && !a.removed) parts.push(n(a.changed, 'changed'))
  return `${who} ${parts.join(', ')}`
}

/**
 * The project's whiteboard, over the whole app (owner, 2026-10-29: 在 Project 裡面打開來一個類似的製作流程圖或是工程圖
 * 的白板 … 裡面所有參與的使用者以及 AI 都可以來操作, 並且共同分享, 共同合作). Everyone in the room sees the same board
 * and every change as it is made; their agents draw on it with `avengents board <id>`.
 *
 * A room has several boards (2026-10-30): the open one's name heads the screen, and pressing it opens the menu of
 * them all — pick one, rename one, make a new one.
 */
export function BoardScreen({ convId, title, onClose }: { convId: string; title: string; onClose: () => void }) {
  const [which, setWhich] = useState(openBoardSel)
  useEffect(() => onBoardNav(() => setWhich(openBoardSel())), [])
  const phone = usePhone()
  return createPortal(
    <BoardPane
      convId={convId}
      title={title}
      which={which}
      onPick={selectBoard}
      phone={phone}
      lead={
        <button type="button" className="bd-back" onClick={onClose} aria-label="Back to the chat">
          <Icon name="chevron_left" size={22} />
          <span>Back</span>
        </button>
      }
    />,
    document.body,
  )
}

/**
 * A phone, whichever way it is held: its SCREEN's short side. Not the window's — held sideways a phone's window is wider
 * than a narrow desktop's (932px) and only 430px tall, and the keyboard moves the window too; the screen's sides only
 * swap (owner, 2026-11-02: 手機橫放的時候, 這個白板會切成一半 … 直接讓白板佔滿全部的螢幕 … 不管手機是橫放還是直放).
 */
export function phoneScreen(): boolean {
  const s = typeof screen === 'undefined' ? null : screen
  const short = s ? Math.min(s.width, s.height) : 0
  return short > 0 && short < 600
}

const phoneBoard = () => window.innerWidth < 700 || phoneScreen()

/**
 * A phone's board: its tools along the bottom, a smaller first view. A narrow window, or a phone held either way — sideways
 * its 430px leave no room for the tools' column.
 */
export function usePhone(): boolean {
  const [phone, setPhone] = useState(phoneBoard)
  useEffect(() => {
    const on = () => setPhone(phoneBoard())
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [])
  return phone
}

/**
 * One of a room's boards with everything around it — its name and the menu of the room's boards, the canvas, the
 * room's chat over it, who changed what. Over the whole app (BoardScreen), or over an agent's session as its drawer
 * (BoardDrawer, `docked`): there the board takes only the keys pressed in it — from the moment it opens — and `room`
 * names the room in the header (a choice, when the agent is in several).
 */
export function BoardPane({
  convId,
  title,
  which,
  onPick,
  phone,
  lead,
  room,
  tail,
  docked,
  covers,
  topSwipe,
}: {
  convId: string
  title: string
  which: string
  onPick: (board: string) => void
  phone: boolean
  /** The header's first control: the screen's Back, the drawer's close. */
  lead: ReactNode
  /** In place of the room's name beside the board's. */
  room?: ReactNode
  /** After "Saved": the drawer's way to the full screen. */
  tail?: ReactNode
  docked?: boolean
  /** The drawer covers the whole screen (a phone, a narrow window), not only the session beside the rail. */
  covers?: boolean
  /** The drawer's header is pulled to close it, as its handle was pulled to open it. */
  topSwipe?: { onTouchStart: (e: TouchEvent) => void; onTouchMove: (e: TouchEvent) => void }
}) {
  const board = useBoard(convId, which)
  const { boards, put } = useBoards(convId)
  const [menu, setMenu] = useState(false)
  const nameBtn = useRef<HTMLButtonElement>(null)
  const root = useRef<HTMLDivElement>(null)
  // The drawer is over the conversation: what is typed now is for the board, not the composer hidden under it.
  useEffect(() => {
    if (docked) root.current?.focus({ preventScroll: true })
  }, [docked])
  const current = boards?.find((b) => b.board === which)
  const name = current?.name ?? (which === MAIN_BOARD ? MAIN_NAME : '')
  const saveLock = (edit: EditMode, editors?: string[]) => setBoardLock(convId, which, edit, editors).then((r) => void put(r.board))
  // What is on the board in front of the person is seen; another board changed since they looked dots the name.
  useMarkSeen(convId, which, board.rev, board.status === 'ready')
  const me = chatv1SelfUser()
  const elsewhere = (boards ?? []).some((b) => b.board !== which && isNews(b, me))
  // The project's chat over the board (owner, 2026-10-30): one subscription for the window and the button's count.
  const { chat, chatOpen, setChatOpen, unread } = useRoomChat(convId)

  // A link to a board this room does not have (or no longer has): the room's first board, and a word about it.
  const [gone, setGone] = useState(false)
  useEffect(() => {
    if (board.status !== 'missing') return
    setGone(true)
    onPick(MAIN_BOARD)
  }, [board.status, onPick])
  // The room's names (the roster sheet reads the same query), so a change says WHO made it — an agent by its own name.
  const membersQ = useQuery({ queryKey: ['proj-members', convId], queryFn: () => getProjectMembers(convId) })
  const nameOf = (user: string, agent?: string): string => {
    const rows = membersQ.data?.members ?? []
    if (agent) return rows.find((m) => m.kind === 'agent' && m.channel_hash === agent)?.label ?? 'An agent'
    const h = rows.find((m) => m.kind !== 'agent' && m.user === user)
    return h?.label ?? h?.email ?? 'Someone'
  }
  return (
    <div
      ref={root}
      className={`bd-screen${docked ? ' bd-drawer' : ''}${covers ? ' bd-cover' : ''}${phone ? ' bd-phone' : ''}`}
      role={docked ? 'complementary' : 'dialog'}
      aria-label={`${name || 'Whiteboard'} — ${title}`}
      // Over a conversation, a press on the board makes its keys the board's again (BoardCanvas `docked`): nothing on a
      // board takes focus by itself, so the drawer does.
      {...(docked
        ? {
            tabIndex: -1,
            onPointerDownCapture: (e: ReactPointerEvent<HTMLDivElement>) => {
              if (!e.currentTarget.contains(document.activeElement)) e.currentTarget.focus({ preventScroll: true })
            },
          }
        : {})}
    >
      <div className="bd-top" {...topSwipe}>
        {lead}
        <div className="bd-title">
          <button
            ref={nameBtn}
            type="button"
            className={`bd-title-btn${menu ? ' on' : ''}`}
            aria-haspopup="dialog"
            aria-expanded={menu}
            title={elsewhere ? 'Boards in this project — another board has changed' : 'Boards in this project'}
            onClick={() => setMenu((m) => !m)}
          >
            <Icon name="dashboard" size={18} />
            <span className="bd-title-name">{name || '…'}</span>
            <Icon name="expand_more" size={18} />
            {elsewhere && <span className="bd-news-dot bd-on-title" role="img" aria-label="Another board has changed" />}
          </button>
          {room ?? (!phone && <span className="bd-title-kind">{title}</span>)}
        </div>
        <span className={`bd-saving bd-${board.saving}`} aria-live="polite">
          {board.saving === 'saving' ? 'Saving…' : board.saving === 'offline' ? 'Not saved yet — retrying' : 'Saved'}
        </span>
        <EditLock convId={convId} what="board" info={current} onSave={saveLock} className="bd-edit-lock" />
        {tail}
      </div>
      {menu && boards && (
        <BoardMenu
          convId={convId}
          project={title}
          boards={boards}
          current={which}
          me={me}
          anchor={nameBtn.current}
          phone={phone}
          onPick={onPick}
          onPut={put}
          onClose={() => setMenu(false)}
        />
      )}
      {/* One canvas per board: the camera, selection and tool start fresh on the board picked. */}
      <BoardCanvas
        key={which}
        board={board}
        phone={phone}
        docked={!!docked}
        convId={convId}
        boardId={which}
        readOnly={!mayChange(current, me)}
        ask={{ boardName: name || MAIN_NAME, agents: mentionRowsOfMembers(membersQ.data?.members ?? [], []), onOpenChat: () => setChatOpen(true) }}
      />
      <button
        type="button"
        className={`bd-chat-btn${chatOpen ? ' on' : ''}`}
        title="Project chat"
        aria-label={unread ? `Project chat, ${unread} new` : 'Project chat'}
        aria-expanded={chatOpen}
        onClick={() => setChatOpen((o) => !o)}
      >
        <Icon name="chat" size={20} />
        {unread > 0 && <span className="bd-chat-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {chatOpen && (
        <BoardChat
          convId={convId}
          from={{ board: which, name: name || MAIN_NAME }}
          project={title}
          agents={mentionRowsOfMembers(membersQ.data?.members ?? [], [])}
          messages={chat.messages}
          loading={chat.loading}
          hasOlder={chat.hasOlder}
          loadingOlder={chat.loadingOlder}
          loadOlder={() => void chat.loadOlder()}
          onClose={() => setChatOpen(false)}
        />
      )}
      {board.activity && (
        <div className={`bd-activity${board.activity.via ? ' bd-by-agent' : ''}`} role="status">
          <Icon name={board.activity.via ? 'smart_toy' : 'group'} size={16} />
          <span>{activityText(board.activity, nameOf)}</span>
          <button type="button" aria-label="Close" onClick={board.clearActivity}>
            <Icon name="close" size={14} />
          </button>
        </div>
      )}
      {(board.error || gone) && (
        <div className="bd-toast" role="alert">
          <span>{board.error ?? 'That board is not in this project — here is its first board.'}</span>
          <button
            type="button"
            aria-label="Close"
            onClick={() => {
              board.clearError()
              setGone(false)
            }}
          >
            <Icon name="close" size={16} />
          </button>
        </div>
      )}
    </div>
  )
}
